import { useEffect } from 'react';
import { useLayoutEffect } from 'react';
import { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useUser } from './user/UserProvider';
import { classNames } from 'libs/react';
import { useMeasure } from 'libs/react';
import { useState } from 'libs/react';
import { computeVisibleTabs } from 'helpers/tabs';
import { slugifyTabLabel } from 'helpers/tabs';
import { Closeable } from 'components/util/Closeable';
import { DropdownMenu } from 'components/DropdownMenu';
import { TabBar } from 'components/TabBar';
import { TabBarButton } from 'components/TabBarButton';
import { TabPanel } from 'components/TabPanel';
import { React } from 'libs/react';
import './TabPanelGroup.scss';
/**
 * Flattens the children into the visible panels: unwraps fragments and
 * permission wrappers (`<Requires>`) at any depth, and drops disabled panels.
 * Each panel gets a stable key, from its `id` or its label.
 * @function collectPanels
 * @since 1.0.0
 * @hidden
 */
function collectPanels(children, can) {
    const panels = [];
    const visit = (nodes) => {
        React.Children.forEach(nodes, (child) => {
            if (React.isValidElement(child) == false) {
                return;
            }
            const element = child;
            const props = element.props;
            if (element.type === React.Fragment) {
                visit(props.children);
                return;
            }
            if (element.type !== TabPanel && props.permission !== undefined) {
                if (can(props.permission, props.operator)) {
                    visit(props.children);
                }
                return;
            }
            if (props.disabled) {
                return;
            }
            panels.push({
                id: props.id,
                element,
                label: props.label,
                picto: props.picto,
                style: props.style
            });
        });
    };
    visit(children);
    const used = new Set();
    panels.forEach((panel, index) => {
        let id = panel.id ||
            (typeof panel.label == 'string' && slugifyTabLabel(panel.label)) ||
            String(index);
        if (used.has(id)) {
            id = `${id}-${index}`;
        }
        used.add(id);
        panel.id = id;
    });
    return panels;
}
/**
 * @component TabPanelGroup
 * @since 1.0.0
 */
export function TabPanelGroup(props) {
    var _a, _b;
    const { style, syncUrl } = Object.assign({ style: 'normal' }, props);
    const user = useUser();
    const history = useHistory();
    const location = useLocation();
    const urlParam = syncUrl ? (syncUrl === true ? 'tab' : syncUrl) : null;
    const panels = collectPanels(props.children, user.can);
    const [internalId, setInternalId] = useState((_a = props.defaultTab) !== null && _a !== void 0 ? _a : null);
    const urlValue = urlParam
        ? new URLSearchParams(location.search).get(urlParam)
        : null;
    let selectedId = internalId;
    if (props.selectedTab !== undefined) {
        selectedId = props.selectedTab;
    }
    else if (props.selectedTabIndex !== undefined) {
        selectedId = (_b = panels[props.selectedTabIndex]) === null || _b === void 0 ? void 0 : _b.id;
    }
    else if (urlValue !== null) {
        selectedId = urlValue;
    }
    let selectedIndex = panels.findIndex((panel) => panel.id === selectedId);
    if (selectedIndex === -1) {
        selectedIndex = 0;
    }
    const selectedPanel = panels[selectedIndex];
    /**
     * Writes the tab in the URL, keeping the other parameters and the history
     * state (replace, so the back button leaves the screen).
     * @function writeUrl
     * @since 1.0.0
     * @hidden
     */
    function writeUrl(id) {
        if (urlParam == null) {
            return;
        }
        // From window, not the router: some screens clean the query string with
        // window.history directly (OAuth returns) and the router does not see it
        const params = new URLSearchParams(window.location.search);
        params.set(urlParam, id);
        history.replace({
            pathname: location.pathname,
            search: `?${params.toString()}`,
            hash: location.hash,
            state: location.state
        });
    }
    /*
     * A tab chosen by the parent (selectedTab, defaultTab) goes in the URL once
     * it exists; a requested tab that is not rendered yet (conditional tab,
     * data still loading) leaves the URL alone.
     */
    const isRequestedTabShown = selectedId != null && (selectedPanel === null || selectedPanel === void 0 ? void 0 : selectedPanel.id) === selectedId;
    useEffect(() => {
        if (isRequestedTabShown && urlValue !== selectedId) {
            writeUrl(selectedId);
        }
    }, [isRequestedTabShown, selectedId, urlValue]);
    /**
     * @function select
     * @since 1.0.0
     * @hidden
     */
    function select(id) {
        setInternalId(id);
        setMenuOpen(false);
        if (props.onSelectTab) {
            props.onSelectTab(id);
        }
        if (props.setSelectedTabIndex) {
            props.setSelectedTabIndex(panels.findIndex((panel) => panel.id === id));
        }
        writeUrl(id);
    }
    /*
     * Overflow: every tab is rendered once in a hidden row to read its natural
     * width; the ones that do not fit in the bar go in the "More" menu.
     */
    const barRef = useRef(null);
    const measureRef = useRef(null);
    const [bounds] = useMeasure(barRef);
    const [measured] = useMeasure(measureRef);
    const [barWidth, setBarWidth] = useState(0);
    const [widths, setWidths] = useState([]);
    const [moreWidth, setMoreWidth] = useState(0);
    const [menuOpen, setMenuOpen] = useState(false);
    const signature = panels
        .map((panel) => panel.id + (typeof panel.label == 'string' ? panel.label : ''))
        .join('|');
    // Read synchronously (before paint) rather than waiting for the observers,
    // which only trigger the next measure
    useLayoutEffect(() => {
        var _a;
        const bar = barRef.current;
        const node = measureRef.current;
        if (bar == null || node == null) {
            return;
        }
        const barStyle = window.getComputedStyle(bar);
        setBarWidth(bar.clientWidth -
            parseFloat(barStyle.paddingLeft) -
            parseFloat(barStyle.paddingRight));
        const buttons = Array.from(node.children);
        const more = buttons.pop();
        setWidths(buttons.map((button) => button.getBoundingClientRect().width));
        setMoreWidth((_a = more === null || more === void 0 ? void 0 : more.getBoundingClientRect().width) !== null && _a !== void 0 ? _a : 0);
    }, [signature, bounds.width, measured.width]);
    // A pixel of slack absorbs the border dropped on non-first tabs
    const { visible, overflow } = widths.length === panels.length
        ? computeVisibleTabs(widths, barWidth - 2, moreWidth, selectedIndex)
        : { visible: panels.map((_, index) => index), overflow: [] };
    /*
     * Keyboard: arrows, Home and End move between all tabs, including the ones
     * in the menu, and keep the focus on the selected tab.
     */
    const focusSelected = useRef(false);
    useEffect(() => {
        var _a, _b;
        if (focusSelected.current) {
            focusSelected.current = false;
            (_b = (_a = barRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.tab-bar-button--is-selected')) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [selectedPanel === null || selectedPanel === void 0 ? void 0 : selectedPanel.id]);
    /**
     * @function onKeyDown
     * @since 1.0.0
     * @hidden
     */
    function onKeyDown(event) {
        if (menuOpen) {
            if (event.key === 'Escape') {
                setMenuOpen(false);
            }
            return;
        }
        let index = null;
        switch (event.key) {
            case 'ArrowLeft':
                index = Math.max(0, selectedIndex - 1);
                break;
            case 'ArrowRight':
                index = Math.min(panels.length - 1, selectedIndex + 1);
                break;
            case 'Home':
                index = 0;
                break;
            case 'End':
                index = panels.length - 1;
                break;
        }
        if (index === null || index === selectedIndex) {
            return;
        }
        event.preventDefault();
        focusSelected.current = true;
        select(panels[index].id);
    }
    const renderButton = (panel, onPress) => (React.createElement(TabBarButton, { key: panel.id, style: panel.style, label: panel.label, picto: panel.picto, selected: panel === selectedPanel, onPress: onPress }));
    const moreLabel = (count) => `${t('More')} (${count})`;
    const className = classNames('tab-panel-group', 'tab-panel-group--' + style, { 'tab-panel-group--has-overflow': overflow.length > 0 }, props.className);
    return (React.createElement("div", { className: className },
        React.createElement("div", { className: "tab-panel-group-head" },
            React.createElement(TabBar, { ref: barRef, onKeyDown: onKeyDown },
                visible.map((index) => renderButton(panels[index], () => select(panels[index].id))),
                overflow.length > 0 && (React.createElement(Closeable, { onClose: () => setMenuOpen(false) },
                    React.createElement("div", { className: "tab-panel-group-more" },
                        React.createElement(TabBarButton, { className: "tab-panel-group-more-button", label: moreLabel(overflow.length), badge: "chevron-down", onPress: () => setMenuOpen((open) => !open) }),
                        menuOpen && (React.createElement(DropdownMenu, { className: "tab-panel-group-more-menu", items: overflow.map((index) => ({
                                value: panels[index].id,
                                label: panels[index].label
                            })), onSelect: select })))))),
            React.createElement("div", { className: "tab-panel-group-measure", "aria-hidden": true },
                React.createElement("div", { ref: measureRef, className: "tab-bar" },
                    panels.map((panel) => renderButton(panel)),
                    React.createElement(TabBarButton, { className: "tab-panel-group-more-button", label: moreLabel(panels.length), badge: "chevron-down" })))),
        React.createElement("div", { className: "tab-panel-group-body" }, selectedPanel && (React.createElement(React.Fragment, { key: selectedPanel.id }, selectedPanel.element)))));
}
